import { type Component, createSignal, Show, onCleanup } from 'solid-js';
import './styles/global.css';
import LandingPage from './components/LandingPage';
import ChatPage from './components/ChatPage';
import { createSessionManager } from './services/session-manager';

/**
 * Application state machine.
 *   'landing'    — user is on the landing page
 *   'chatting'   — active chat session (waiting, matched, connecting, connected, ended)
 */
type AppState = 'landing' | 'chatting';

const App: Component = () => {
  const [appState, setAppState] = createSignal<AppState>('landing');
  const [localStream, setLocalStream] = createSignal<MediaStream | null>(null);

  // Create session manager (SolidJS reactive)
  const session = createSessionManager();

  // Expose online count from signaling server (falls back to a placeholder until connected)
  const onlineCount = () => {
    const signalingCount = session.onlineCount;
    if (typeof signalingCount === 'function') {
      const val = signalingCount();
      return val > 0 ? val : 142; // Show 142 as fallback until real data arrives
    }
    return 142;
  };

  /**
   * Called when the user grants camera/mic permissions on the landing page.
   * Receives the live MediaStream so we can pass tracks to the session manager.
   */
  const handleStartChatting = (stream: MediaStream) => {
    setLocalStream(stream);

    // Pass audio track to the session manager for WebRTC
    const audioTrack = stream.getAudioTracks()[0] ?? null;
    if (audioTrack) {
      session.setLocalAudioTrack(audioTrack);
    }

    // Pass video track as the raw video (for stage 3 / reveal)
    const videoTrack = stream.getVideoTracks()[0] ?? null;
    if (videoTrack) {
      session.setRawVideoTrack(videoTrack);
    }

    // Start the session — connect to signaling and join the queue
    session.startSession(
      {
        // For MVP, attributes are minimal / auto-detected
        age: 25,
        gender: 'other',
        geoRegion: 'US',
        language: 'en',
      },
      // No preferences for now
      undefined,
    );

    setAppState('chatting');
  };

  /**
   * Called when the user ends the session and returns to the landing page.
   */
  const handleEndSession = () => {
    // Stop local media tracks
    const stream = localStream();
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setLocalStream(null);

    // Ensure session is ended
    session.endSession();

    setAppState('landing');
  };

  // Cleanup on unmount
  onCleanup(() => {
    const stream = localStream();
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    session.endSession();
  });

  return (
    <>
      <Show when={appState() === 'landing'}>
        <LandingPage
          onlineCount={onlineCount}
          onStartChatting={handleStartChatting}
        />
      </Show>

      <Show when={appState() === 'chatting'}>
        <ChatPage
          session={session}
          localStream={localStream()}
          onEnd={handleEndSession}
        />
      </Show>
    </>
  );
};

export default App;
